/* eslint comma-dangle: "off" */
/* eslint space-before-function-paren: "off" */
import Vue from "vue";
import Router from "vue-router";
import { routeConfig } from "../config/routeConfig";
import AuthUtils from "../utils/auth.utils";
import {
  Reservations,
  Analytics,
  Contacts,
  Settings,
  Tabs,
  AppLayout,
  Setup,
  PointOfSale,
  Overview,
} from "./views/index";
import {
  Information,
  Accommodations,
  RateManagement,
  BookingPackages,
  MenuCategories,
  Inventory,
  TaxesAndFees,
  DiscountCodes,
  General,
  Employees,
  Help,
  Integrations,
  Account,
  ChannelManager,
  // Billing,
  TagManagement,
} from "./views/settingsViews/index";

import {
  Transactions,
  Arrivals,
  Departures,
  Occupancy,
  Summary,
  Bookings,
  Sales,
  Products,
} from "./views/analyticsViews/index";

// Import new auth components
import AuthLayout from "../views/auth/AuthLayout.vue";
import AuthLogin from "../views/auth/Login.vue";
import AuthRegister from "../views/auth/Register.vue";
import AuthForgotPassword from "../views/auth/ForgotPassword.vue";
import AuthResetPassword from "../views/auth/ResetPassword.vue";

import { store } from "../modules/index";
import { defaultLocale } from "../config/i18n";

Vue.use(Router);

export const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      redirect: `/${defaultLocale || "en"}`,
    },
    {
      path: "/:locale",
      component: {
        template: "<router-view />",
      },
      children: [
        {
          path: "",
          name: "Home",
          redirect: function (to) {
            const locale = to.params.locale || "en";
            return `/${locale}/auth/login`;
          },
        },
        // New auth routes
        {
          path: "auth",
          component: AuthLayout,
          meta: { guest: true },
          children: [
            {
              path: "login",
              name: "AuthLogin",
              component: AuthLogin,
              meta: { guest: true },
            },
            {
              path: "register",
              name: "AuthRegister",
              component: AuthRegister,
              meta: { guest: true },
            },
            {
              path: "forgot-password",
              name: "AuthForgotPassword",
              component: AuthForgotPassword,
              meta: { guest: true },
            },
            {
              path: "reset-password",
              name: "AuthResetPassword",
              component: AuthResetPassword,
              meta: { guest: true },
            },
          ],
        },
        {
          path: "dashboard",
          name: "DashboardHome",
          component: AppLayout,
          children: [
            {
              path: "",
              component: Overview,
            },
          ],
          meta: { requiresAuth: true },
        },
        {
          path: "reservations",
          name: "Reservations",
          component: AppLayout,
          children: [
            {
              path: "",
              component: Reservations,
            },
          ],
          meta: { requiresAuth: true },
        },
        {
          path: "tabs",
          name: "Tabs",
          component: AppLayout,
          children: [
            {
              path: "",
              component: Tabs,
            },
          ],
          meta: { requiresAuth: true },
        },
        {
          path: "setup",
          name: "Setup",
          component: AppLayout,
          children: [
            {
              path: "",
              component: Setup,
            },
          ],
          meta: { requiresAuth: true },
        },
        {
          path: "pos",
          name: "POS",
          component: AppLayout,
          children: [
            {
              path: "",
              component: PointOfSale,
            },
          ],
          meta: { requiresAuth: true, requiresPOS: true },
        },
        {
          path: "analytics",
          component: AppLayout,
          meta: { requiresAuth: true },
          children: [
            {
              path: "",
              component: Analytics,
              children: [
                {
                  path: "",
                  redirect: "summary",
                },
                {
                  path: "summary",
                  name: "Summary",
                  component: Summary,
                },
                {
                  path: "transactions",
                  name: "Transactions",
                  component: Transactions,
                },
                {
                  path: "occupancy",
                  name: "Occupancy",
                  component: Occupancy,
                },
                {
                  path: "bookings",
                  name: "bookings",
                  component: Bookings,
                },
                {
                  path: "arrivals",
                  name: "Arrivals",
                  component: Arrivals,
                },
                {
                  path: "departures",
                  name: "Departures",
                  component: Departures,
                },
                {
                  path: "sales",
                  name: "Sales",
                  component: Sales,
                  meta: { requiresPOS: true },
                },
                {
                  path: "products",
                  name: "Products",
                  component: Products,
                  meta: { requiresPOS: true },
                },
              ],
            },
          ],
        },
        {
          path: "contacts",
          name: "Contacts",
          component: AppLayout,
          children: [
            {
              path: "",
              component: Contacts,
            },
          ],
          meta: { requiresAuth: true },
        },
        {
          path: "settings",
          component: AppLayout,
          meta: { requiresAuth: true },
          children: [
            {
              path: "",
              component: Settings,
              children: [
                {
                  path: "",
                  redirect: "information",
                },
                ...Object.entries(routeConfig.settings)
                  .filter(([_, config]) => config.enabled)
                  .map(([key, config]) => {
                    const componentMap = {
                      Information,
                      Accommodations,
                      RateManagement,
                      BookingPackages,
                      MenuCategories,
                      Inventory,
                      TaxesAndFees,
                      DiscountCodes,
                      General,
                      Employees,
                      Help,
                      Integrations,
                      Account,
                      ChannelManager,
                      // Billing,
                      TagManagement,
                    };

                    const posRequiredRoutes = ["inventory", "menuCategories"];
                    const route = {
                      path: config.path,
                      name: config.title,
                      component: componentMap[config.component],
                    };

                    // Add POS requirement meta for specific routes
                    if (posRequiredRoutes.includes(key)) {
                      route.meta = { requiresPOS: true };
                    }

                    return route;
                  }),
              ],
            },
          ],
        },
      ],
    },
  ],
});

// Enhanced auth guard with better error handling and token validation

// Cache the timestamp of the last successful token check to avoid spamming the API
let lastTokenValidation = 0;
const TOKEN_VALIDATION_INTERVAL = 5 * 60 * 1000; // 5 minutes

router.beforeEach(async (to, from, next) => {
  Vue.prototype.$Progress.start();

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresPOS = to.matched.some((record) => record.meta.requiresPOS);
  const isGuest = to.matched.some((record) => record.meta.guest);
  const isAuthenticated = AuthUtils.isAuthenticated();

  // Handle authentication requirements
  if (requiresAuth && !isAuthenticated) {
    AuthUtils.redirectToLogin(to.fullPath);
    return;
  }

  // If the route is protected, we have a token, and it's been more than TOKEN_VALIDATION_INTERVAL
  // since the last verification, ping the backend to ensure the token is still valid.
  if (requiresAuth && isAuthenticated) {
    const now = Date.now();
    if (now - lastTokenValidation > TOKEN_VALIDATION_INTERVAL) {
      try {
        // Lazy–load to prevent circular dependencies
        const { AuthService } = await import("../services/auth.service");
        await AuthService.verifyToken();
        lastTokenValidation = now;
      } catch (e) {
        // Token is invalid – clear auth data and redirect
        AuthUtils.clearAuthData();
        AuthUtils.redirectToLogin(to.fullPath);
        return;
      }
    }
  }

  // Handle POS access requirements
  if (requiresPOS && isAuthenticated) {
    const hasPOSAccess = store.getters["subscription/hasPOSAccess"];
    if (!hasPOSAccess) {
      const locale = to.params.locale || defaultLocale || "en";
      // Redirect to settings/billing with a message
      next(`/${locale}/settings/billing`);
      // Show notification about POS access
      Vue.prototype.$bvToast.toast(
        "POS access is required. Please upgrade your subscription.",
        {
          title: "Access Denied",
          variant: "warning",
          solid: true,
        }
      );
      return;
    }
  }

  // Also check for POS-required settings routes by path
  const posRequiredPaths = ["inventory", "menucategories"];
  const currentPath = to.path.split("/").pop();
  if (isAuthenticated && posRequiredPaths.includes(currentPath)) {
    const hasPOSAccess = store.getters["subscription/hasPOSAccess"];
    if (!hasPOSAccess) {
      const locale = to.params.locale || defaultLocale || "en";
      next(`/${locale}/settings/billing`);
      Vue.prototype.$bvToast.toast(
        "POS access is required for this feature. Please upgrade your subscription.",
        {
          title: "Access Denied",
          variant: "warning",
          solid: true,
        }
      );
      return;
    }
  }

  // Redirect authenticated users away from guest pages
  if (isGuest && isAuthenticated) {
    const locale = to.params.locale || defaultLocale || "en";
    next(`/${locale}/dashboard`);
    return;
  }

  // Update store authentication state if user is authenticated
  if (isAuthenticated && !store.getters.isAuthenticated) {
    store.dispatch("initializeAuth");
  }

  next();
});

router.afterEach(() => {
  Vue.prototype.$Progress.finish();
});

router.onError((error) => {
  Vue.prototype.$Progress.fail();
  console.error("Router error:", error);

  // Handle authentication errors
  if (error.message?.includes("auth") || error.status === 401) {
    AuthUtils.clearAuthData();
    AuthUtils.redirectToLogin();
  }
});

export default router;
